import React, { useEffect, useId, useRef, useState } from 'react'
import {
    autoUpdate,
    FloatingPortal,
    offset,
    shift,
    useDismiss,
    useFloating,
    useInteractions
} from '@floating-ui/react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '@/hooks/useAuth'
import { MapType, useMapType } from '@/component/map/MapContext'
import { useLanguageOptions } from '@/locales/useLanguageOptions.ts'
import { useIsMobileWidth } from '@/store/SideNavStore.ts'
import { useAccountProfile } from '@/component/layout/TopNav/accountProfile.ts'
import AlarmSoundRow from '@/component/layout/TopNav/AlarmSoundRow.tsx'
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    GlobeIcon,
    LogoutIcon,
    MapPinIcon,
    UserIcon
} from '@/component/layout/ShellIcons.tsx'

type Submenu = 'language' | 'map'

const MAP_NAMES: Record<MapType, string> = { google: 'Google', baidu: 'Baidu' }
const SUBMENU_HOVER_DELAY = 200
const ITEM_SELECTOR = '[data-menu-item]'
const SUBMENU_ITEM_SELECTOR = '[data-submenu-item]'

/**
 * 상단바 계정 메뉴: 마이페이지 · 언어 › · 지도 › · 긴급 알림음 켜고 끄기 · 로그아웃.
 * 하위 메뉴는 왼쪽으로 열고, 599px 이하에서는 아래 시트로 띄워 하위 메뉴를 같은 시트 안에서 바꿔 보인다.
 */
const AccountMenu: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { logout } = useAuth()
    const { name, email, initial } = useAccountProfile()
    const { mapType, setMapType } = useMapType()
    const language = useLanguageOptions()
    const isMobile = useIsMobileWidth()
    const [isOpen, setIsOpen] = useState(false)
    const [submenu, setSubmenu] = useState<Submenu | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const panelRef = useRef<HTMLDivElement | null>(null)
    const hoverTimer = useRef<ReturnType<typeof setTimeout>>()
    const menuId = useId()

    const close = (returnFocus: boolean) => {
        clearTimeout(hoverTimer.current)
        setIsOpen(false)
        setSubmenu(null)
        if (returnFocus) buttonRef.current?.focus()
    }

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => (open ? setIsOpen(true) : close(false)),
        placement: 'bottom-end',
        middleware: [offset(8), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate
    })
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useDismiss(context, { escapeKey: false })
    ])

    useEffect(() => {
        clearTimeout(hoverTimer.current)
        setIsOpen(false)
        setSubmenu(null)
    }, [location.pathname])

    const listItems = (selector: string) => [
        ...(panelRef.current?.querySelectorAll<HTMLElement>(selector) ?? [])
    ]

    const focusAt = (selector: string, index: number) => {
        const list = listItems(selector)
        if (list.length) list[(index + list.length) % list.length].focus()
    }

    const openSubmenu = (next: Submenu, withFocus: boolean) => {
        clearTimeout(hoverTimer.current)
        setSubmenu(next)
        if (!withFocus) return
        requestAnimationFrame(() => {
            const list = listItems(SUBMENU_ITEM_SELECTOR)
            ;(list.find((el) => el.getAttribute('aria-checked') === 'true') ?? list[0])?.focus()
        })
    }

    const closeSubmenu = () => {
        const parent = submenu
        setSubmenu(null)
        requestAnimationFrame(() =>
            panelRef.current?.querySelector<HTMLElement>(`[data-submenu-parent="${parent}"]`)?.focus()
        )
    }

    const hoverRow = (next: Submenu | null) => {
        if (isMobile) return
        clearTimeout(hoverTimer.current)
        if (next === submenu) return
        hoverTimer.current = setTimeout(() => setSubmenu(next), SUBMENU_HOVER_DELAY)
    }

    const onButtonKeyDown = (e: React.KeyboardEvent) => {
        if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
            e.preventDefault()
            if (isOpen) {
                close(false)
                return
            }
            setIsOpen(true)
            requestAnimationFrame(() => focusAt(ITEM_SELECTOR, 0))
        } else if (e.key === 'Escape' && isOpen) {
            e.preventDefault()
            close(true)
        }
    }

    const onPanelKeyDown = (e: React.KeyboardEvent) => {
        const active = document.activeElement as HTMLElement | null
        const inSubmenu = !!active?.matches(SUBMENU_ITEM_SELECTOR)
        const selector = inSubmenu ? SUBMENU_ITEM_SELECTOR : ITEM_SELECTOR
        const list = listItems(selector)
        const current = active ? list.indexOf(active) : -1
        const moves: Record<string, number> = {
            ArrowDown: current + 1,
            ArrowUp: current - 1,
            Home: 0,
            End: list.length - 1
        }
        if (e.key in moves) {
            e.preventDefault()
            focusAt(selector, moves[e.key])
        } else if (e.key === 'Escape' || (e.key === 'ArrowRight' && inSubmenu && !isMobile)) {
            e.preventDefault()
            if (submenu) closeSubmenu()
            else close(true)
        } else if (e.key === 'Tab') {
            close(false)
        }
    }

    const onLogout = async () => {
        close(false)
        await logout()
        toast.success(t('topNav.menu.logout.success'))
        navigate('/login')
    }

    const submenuLabels: Record<Submenu, string> = {
        language: t('topNav.menu.language'),
        map: t('topNav.menu.map')
    }
    const mapOptions: { id: MapType; label: string }[] = [
        { id: 'google', label: t('topNav.map.google') },
        { id: 'baidu', label: t('topNav.map.baidu') }
    ]

    const renderOption = (key: string, label: string, checked: boolean, onSelect: () => void) => (
        <button
            key={key}
            type={'button'}
            role={'menuitemradio'}
            aria-checked={checked}
            data-submenu-item
            className={'account-menu__option'}
            onClick={() => {
                close(true)
                if (!checked) onSelect()
            }}
        >
            <span>{label}</span>
            {checked && <CheckIcon className={'account-menu__check'} />}
        </button>
    )

    const renderOptions = (id: Submenu) => (
        <>
            {id === 'language'
                ? language.languages.map((option) =>
                    renderOption(option.id, option.label, option.id === language.current.id, () =>
                        language.changeLanguage(option.id)
                    )
                )
                : mapOptions.map((option) =>
                    renderOption(option.id, option.label, option.id === mapType, () => setMapType(option.id))
                )}
            {id === 'map' && <p className={'account-menu__hint'}>{t('topNav.map.hint')}</p>}
        </>
    )

    const renderSubmenuRow = (id: Submenu, icon: React.ReactNode, value: string) => (
        <div className={'account-menu__group'} role={'none'}>
            <button
                type={'button'}
                role={'menuitem'}
                aria-haspopup={'menu'}
                aria-expanded={submenu === id}
                data-menu-item
                data-submenu-parent={id}
                className={`account-menu__item ${submenu === id ? 'account-menu__item--open' : ''}`}
                onMouseEnter={() => hoverRow(id)}
                onMouseLeave={() => clearTimeout(hoverTimer.current)}
                onClick={(e) => {
                    if (submenu === id && !isMobile) setSubmenu(null)
                    else openSubmenu(id, isMobile || e.detail === 0)
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'ArrowLeft' || isMobile) return
                    e.preventDefault()
                    openSubmenu(id, true)
                }}
            >
                {icon}
                <span className={'account-menu__label'}>{submenuLabels[id]}</span>
                <span className={'account-menu__value'}>{value}</span>
                <ChevronRightIcon className={'account-menu__chevron'} />
            </button>
            {submenu === id && !isMobile && (
                <div
                    role={'menu'}
                    aria-label={submenuLabels[id]}
                    className={`account-menu__submenu account-menu__submenu--${id}`}
                    onMouseEnter={() => clearTimeout(hoverTimer.current)}
                >
                    {renderOptions(id)}
                </div>
            )}
        </div>
    )

    const mainView = (
        <>
            <div className={'account-menu__profile'}>
                <strong>{name}</strong>
                {email && <small>{email}</small>}
            </div>
            <div className={'account-menu__divider'} role={'separator'} />
            <button
                type={'button'}
                role={'menuitem'}
                data-menu-item
                className={'account-menu__item'}
                onMouseEnter={() => hoverRow(null)}
                onClick={() => {
                    close(false)
                    navigate('/account')
                }}
            >
                <UserIcon className={'account-menu__icon'} />
                <span className={'account-menu__label'}>{t('topNav.menu.account')}</span>
            </button>
            {renderSubmenuRow('language', <GlobeIcon className={'account-menu__icon'} />, language.current.label)}
            {renderSubmenuRow('map', <MapPinIcon className={'account-menu__icon'} />, MAP_NAMES[mapType])}
            <AlarmSoundRow onHover={() => hoverRow(null)} isSheet={isMobile} />
            <div className={'account-menu__divider'} role={'separator'} />
            <button
                type={'button'}
                role={'menuitem'}
                data-menu-item
                className={'account-menu__item'}
                onMouseEnter={() => hoverRow(null)}
                onClick={onLogout}
            >
                <LogoutIcon className={'account-menu__icon'} />
                <span className={'account-menu__label'}>{t('topNav.menu.logout')}</span>
            </button>
        </>
    )

    const sheetSubView = submenu && (
        <>
            <button
                type={'button'}
                role={'menuitem'}
                data-submenu-item
                className={'account-menu__back'}
                onClick={closeSubmenu}
            >
                <ChevronLeftIcon className={'account-menu__icon'} />
                <span>{submenuLabels[submenu]}</span>
            </button>
            <div className={'account-menu__divider'} role={'separator'} />
            {renderOptions(submenu)}
        </>
    )

    const panelProps = {
        id: menuId,
        role: 'menu',
        'aria-label': t('topNav.accountMenu', { name }),
        ...getFloatingProps({ onKeyDownCapture: onPanelKeyDown })
    }

    return (
        <div className={'top-nav__account-menu'}>
            <button
                ref={(el) => {
                    buttonRef.current = el
                    refs.setReference(el)
                }}
                type={'button'}
                aria-haspopup={'menu'}
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                aria-label={t('topNav.accountMenu', { name })}
                className={`top-nav__account ${isOpen ? 'top-nav__account--open' : ''}`}
                {...getReferenceProps({
                    onClick: () => (isOpen ? close(false) : setIsOpen(true)),
                    onKeyDown: onButtonKeyDown
                })}
            >
                <span className={'top-nav__avatar'} aria-hidden>{initial}</span>
                <span className={'top-nav__account-name'}>{name}</span>
                <ChevronDownIcon className={'top-nav__account-chevron'} />
            </button>

            {isOpen && !isMobile && (
                <FloatingPortal>
                    <div
                        ref={(el) => {
                            panelRef.current = el
                            refs.setFloating(el)
                        }}
                        style={floatingStyles}
                        className={'account-menu z-dropdown'}
                        {...panelProps}
                    >
                        {mainView}
                    </div>
                </FloatingPortal>
            )}

            {isOpen && isMobile && (
                <FloatingPortal>
                    <div className={'account-sheet-layer z-modal'}>
                        <div className={'account-sheet-layer__backdrop'} aria-hidden />
                        <div
                            ref={(el) => {
                                panelRef.current = el
                                refs.setFloating(el)
                            }}
                            className={'account-menu account-menu--sheet'}
                            {...panelProps}
                        >
                            {sheetSubView || mainView}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </div>
    )
}

export default AccountMenu
